import { formatMatchCountSummary, highlightMatch, textIncludesQuery } from './search-highlight';

export type SearchHighlightField = {
  selector: string;
};

export type SearchHighlightConfig = {
  cardSelector: string;
  fields: SearchHighlightField[];
};

export type SearchInputElements = {
  root: HTMLElement;
  input: HTMLInputElement;
  clearBtn: HTMLButtonElement;
  summary: HTMLElement | null;
};

export type SearchInputOptions = {
  inputId?: string;
  highlight?: SearchHighlightConfig;
  syncUrl?: boolean;
  urlParam?: string;
  /** server: no client-side filtering, value is sent via filter apply (GET q=) */
  mode?: 'local' | 'server';
};

const INIT_FLAG = 'searchInputBound';
const DEFAULT_URL_PARAM = 'q';

export const SEARCH_MIN_LENGTH = 3;

export function shouldSubmitSearchQuery(value: string, lastSubmittedSearch: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= SEARCH_MIN_LENGTH;
}

function resolveRoot(root?: ParentNode): ParentNode {
  return root ?? document;
}

export function querySearchInput(
  root?: ParentNode,
  inputId = 'news-search',
): SearchInputElements | null {
  const scope = resolveRoot(root);
  const input = scope.querySelector<HTMLInputElement>(`#${inputId}`);
  const rootEl = input?.closest<HTMLElement>('[data-search-input]') ?? null;
  const clearBtn = rootEl?.querySelector<HTMLButtonElement>('[data-search-clear]') ?? null;
  const summary = rootEl
    ? (scope.querySelector<HTMLElement>(`[data-search-summary][data-search-for="${inputId}"]`) ??
      rootEl
        .closest<HTMLElement>('[data-barba-namespace]')
        ?.querySelector<HTMLElement>('[data-search-summary]') ??
      rootEl.querySelector<HTMLElement>('[data-search-summary]'))
    : null;

  if (!rootEl || !input || !clearBtn) {
    return null;
  }

  return { root: rootEl, input, clearBtn, summary };
}

function ensureOriginalText(element: HTMLElement): string {
  if (!element.dataset.searchOriginal) {
    element.dataset.searchOriginal = element.textContent ?? '';
  }
  return element.dataset.searchOriginal;
}

function restoreField(element: HTMLElement): void {
  if (element.dataset.searchOriginal) {
    element.textContent = element.dataset.searchOriginal;
  }
}

function applyFieldHighlight(element: HTMLElement, query: string): void {
  const original = ensureOriginalText(element);
  element.innerHTML = highlightMatch(original, query);
}

function updateClearButton(clearBtn: HTMLButtonElement, hasValue: boolean): void {
  clearBtn.hidden = !hasValue;
}

const LOAD_MORE_WRAP_SELECTOR =
  '[data-news-load-more-wrap], [data-events-load-more-wrap], [data-education-programs-load-more-wrap]';

function updateLoadMoreVisibility(
  namespace: HTMLElement | null,
  query: string,
  matchCount: number,
  cardSelector?: string,
): void {
  if (!namespace) return;

  const trimmed = query.trim();
  const hideForEmptySearch = Boolean(trimmed) && matchCount === 0;
  const hideForEmptyList = cardSelector
    ? namespace.querySelectorAll(cardSelector).length === 0
    : false;

  namespace.querySelectorAll<HTMLElement>(LOAD_MORE_WRAP_SELECTOR).forEach((wrap) => {
    if (wrap.dataset.loadMoreExhausted === 'true') {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = hideForEmptySearch || hideForEmptyList;
  });
}

function updateSummary(summary: HTMLElement | null, count: number, query: string): void {
  if (!summary) return;
  const trimmed = query.trim();
  if (!trimmed) {
    summary.hidden = true;
    summary.innerHTML = '';
    return;
  }

  summary.hidden = false;
  summary.innerHTML = formatMatchCountSummary(count, trimmed);
}

function syncSearchToUrl(query: string, paramName: string): void {
  const params = new URLSearchParams(window.location.search);
  const trimmed = query.trim();

  if (trimmed) {
    params.set(paramName, trimmed);
  } else {
    params.delete(paramName);
  }

  const queryString = params.toString();
  const nextUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  window.history.replaceState(null, '', nextUrl);
}

function cardMatchesQuery(
  card: HTMLElement,
  query: string,
  highlight: SearchHighlightConfig,
): boolean {
  const fieldElements = highlight.fields
    .map((field) => card.querySelector<HTMLElement>(field.selector))
    .filter((el): el is HTMLElement => el !== null);

  return fieldElements.some((field) => textIncludesQuery(ensureOriginalText(field), query));
}

function shouldFilterClientSide(
  cards: NodeListOf<HTMLElement>,
  query: string,
  highlight: SearchHighlightConfig,
): boolean {
  const trimmed = query.trim();
  if (!trimmed) return false;

  let matchingCards = 0;

  cards.forEach((card) => {
    if (cardMatchesQuery(card, trimmed, highlight)) {
      matchingCards += 1;
    }
  });

  return matchingCards < cards.length;
}

export function applyServerSearchResults(
  root: ParentNode,
  query: string,
  options: {
    inputId?: string;
    highlight: SearchHighlightConfig;
  },
): number {
  const elements = querySearchInput(root, options.inputId);
  if (!elements) return 0;

  return applySearch(elements, query, options.highlight);
}

function applySearch(
  elements: SearchInputElements,
  query: string,
  highlight?: SearchHighlightConfig,
): number {
  const trimmed = query.trim();
  let matchCount = 0;
  const namespace = elements.root.closest<HTMLElement>('[data-barba-namespace]');

  if (!highlight) {
    updateSummary(elements.summary, 0, trimmed);
    updateClearButton(elements.clearBtn, Boolean(trimmed));
    updateLoadMoreVisibility(namespace, trimmed, 0);
    return 0;
  }

  const cards = namespace?.querySelectorAll<HTMLElement>(highlight.cardSelector);

  if (!cards?.length) {
    updateSummary(elements.summary, 0, trimmed);
    updateClearButton(elements.clearBtn, Boolean(trimmed));
    updateLoadMoreVisibility(namespace, trimmed, 0, highlight.cardSelector);
    return 0;
  }

  const filterClientSide = shouldFilterClientSide(cards, trimmed, highlight);

  cards.forEach((card) => {
    const fieldElements = highlight.fields
      .map((field) => card.querySelector<HTMLElement>(field.selector))
      .filter((el): el is HTMLElement => el !== null);

    fieldElements.forEach((field) => restoreField(field));

    const isMatch = !trimmed || cardMatchesQuery(card, trimmed, highlight);

    if (filterClientSide) {
      card.hidden = !isMatch;
    } else {
      card.hidden = false;
    }

    if (isMatch && trimmed) {
      matchCount += 1;
      fieldElements.forEach((field) => applyFieldHighlight(field, trimmed));
    } else if (!trimmed) {
      fieldElements.forEach((field) => restoreField(field));
    }
  });

  updateSummary(elements.summary, matchCount, trimmed);
  updateClearButton(elements.clearBtn, Boolean(trimmed));
  updateLoadMoreVisibility(namespace, trimmed, matchCount, highlight.cardSelector);

  return matchCount;
}

export function initSearchInput(
  root?: ParentNode,
  options: SearchInputOptions = {},
): SearchInputElements | null {
  const inputId = options.inputId ?? 'news-search';
  const elements = querySearchInput(root, inputId);
  if (!elements) return null;

  const { root: searchRoot, input, clearBtn } = elements;
  const urlParam = options.urlParam ?? DEFAULT_URL_PARAM;
  const isServerMode = options.mode === 'server';

  if (searchRoot.dataset[INIT_FLAG] === 'true') {
    return elements;
  }
  searchRoot.dataset[INIT_FLAG] = 'true';

  const runSearch = (value: string): void => {
    if (isServerMode) {
      updateClearButton(clearBtn, Boolean(value.trim()));
      return;
    }

    applySearch(elements, value, options.highlight);
    if (options.syncUrl) {
      syncSearchToUrl(value, urlParam);
    }
  };

  const onClear = (): void => {
    input.value = '';
    runSearch('');
    input.focus();
  };

  clearBtn.addEventListener('click', onClear);

  const onInput = (): void => {
    runSearch(input.value);
  };
  input.addEventListener('input', onInput);

  const urlParams = new URLSearchParams(window.location.search);
  const urlQuery = urlParams.get(urlParam) ?? urlParams.get('search');
  if (urlQuery) {
    input.value = urlQuery;
  }

  runSearch(input.value);

  return elements;
}

export function populateSearchFromURL(
  root?: ParentNode,
  inputId = 'news-search',
  urlParam = DEFAULT_URL_PARAM,
): void {
  const elements = querySearchInput(root, inputId);
  if (!elements) return;

  const urlParams = new URLSearchParams(window.location.search);
  const urlQuery = urlParams.get(urlParam) ?? urlParams.get('search');
  if (urlQuery) {
    elements.input.value = urlQuery;
    updateClearButton(elements.clearBtn, true);
  }
}
